import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';

import type { AoeShape } from '../net/protocol';
import type { GlowLayer } from '@babylonjs/core/Layers/glowLayer';

import { Engine } from '@babylonjs/core/Engines/engine';

import { buildAoeMesh } from './aoeMesh';
import { fxFor, spriteTexture } from './fx';
import { registerGlow } from './glow';

import {
  BLAST_SECONDS,
  BOLT_HEIGHT,
  BOLT_SECONDS,
  boltAt,
  boltHeading,
  defaultColour,
  IMPACT_BASE_DIAMETER,
  IMPACT_SECONDS,
  impactAlpha,
  impactScale,
  parseHex,
  DEFAULT_SHOT_SHAPE,
  projectileVisual,
  type ProjectileShape,
  type ProjectileVisual,
  resolveProjectile,
  TRAIL_MOTE_DIAMETER,
  trailDelay,
  trailMoteScale,
  trailOffset,
  type Vec3,
} from './bolt';

/**
 * Projectiles and impact flashes.
 *
 * The Babylon half of `bolt.ts`, which holds every decision — the shape table, the
 * flight arc, the trail maths and the fallbacks all live there. This is geometry
 * construction and node writes.
 *
 * A cast resolves instantly on the server, so nothing here affects the game: a
 * bolt is decoration over a settled outcome. That is also why it can be skipped
 * entirely under `?safe=1` without changing what happens.
 *
 * Everything is built here and nothing is loaded: there is no asset pipeline, so a
 * frost splinter is a four-sided cone and a lightning crackle is a thin box with a
 * zig-zag wake behind it. `buildHead` is exercised headlessly in `bolts.test.ts` —
 * `NullEngine` builds real geometry and real bounding boxes, which is what makes the
 * "two shapes a player can tell apart" claim measurable rather than asserted.
 */

/** How many bolts may be in the air at once. */
const MAX_BOLTS = 24;

/**
 * The default shape a basic ranged attack flies as, and its colour when the hit was
 * physical.
 *
 * The shape lives in `bolt.ts` beside the weapon table that overrides it, so the shard a
 * monster fires and the shard a bow fires are one declaration. The pale grey is
 * deliberately dull — an untyped shot is the ordinary case, and it must not out-shout a
 * fire one.
 */
const MOB_SHOT_SHAPE = DEFAULT_SHOT_SHAPE;
const MOB_SHOT_RGB: [number, number, number] = [0.78, 0.78, 0.86];

interface Flying {
  visual: ProjectileVisual;
  /** The projectile itself. Absent for `NONE`, which throws nothing. */
  head: Mesh | null;
  /** Wake motes, head-adjacent first. */
  motes: Mesh[];
  /** The flash. Built with the bolt, hidden until arrival. */
  flash: Mesh;
  material: StandardMaterial;
  from: { x: number; z: number };
  to: { x: number; z: number };
  heading: number;
  /** Seconds elapsed. Past BOLT_SECONDS it becomes an impact. */
  age: number;
  impacting: boolean;
}

/**
 * A blast ring: everything inside it was hit at once.
 *
 * Kept in its own list rather than folded into `Flying`, because it is not a projectile
 * and shares none of the interesting parts of one — no arc, no wake, no heading, no
 * impact phase. Giving `Flying` four unused fields and a `kind` discriminator would make
 * every bolt carry the cost of a thing it is not.
 */

/** How high off the floor the ring sits, so it does not z-fight with the ground. */
const BLAST_HEIGHT = 0.06;

interface Blast {
  ring: Mesh;
  material: StandardMaterial;
  /** The final radius, in world units. The footprint opens to exactly this. */
  radius: number;
  age: number;
  /** Which geometry it is, because a corridor grows forward while a circle grows outward. */
  shape: AoeShape;
}

export class BoltField {
  private readonly live: Flying[] = [];
  private readonly blasts: Blast[] = [];

  /**
   * Scratch for the flight arc, shared by every bolt and every mote.
   *
   * One object for the whole field rather than one per bolt: `update` is strictly
   * sequential, so nothing overlaps, and the render loop must not allocate — twenty-four
   * bolts with eight motes each is two hundred short-lived objects a frame otherwise.
   */
  private readonly at: Vec3 = { x: 0, y: 0, z: 0 };

  /**
   * `glow` is how a spell reaches the bloom pass, and it is optional for the same reason
   * every other render feature is: `?glow=0` and safe mode both refuse to build one.
   *
   * **Spells were the half that was missing.** The zone renderer opted its windows and
   * portals in, and nothing opted these in, so a lit window bloomed and a fireball did
   * not — the two most obviously light things in the game disagreeing, because the policy
   * lived in one file and the effects lived in another. See `game/glow.ts`.
   */
  constructor(
    private readonly scene: Scene,
    private readonly glow: GlowLayer | null = null,
  ) {}

  /**
   * Launches a bolt, in the shape the skill's catalogue entry asks for.
   *
   * A weapon skill is a swing — drawing a projectile for it would say the damage came
   * from across the room — so `resolveProjectile` answers `NONE` for one and the
   * caster's cast animation is the whole effect. `NONE` is also what a nova, a shout or
   * a self-buff resolves to: those bloom on the caster instead of crossing the room,
   * and before shapes existed every one of them lobbed a ball at the cursor.
   */
  cast(
    from: { x: number; z: number },
    to: { x: number; z: number },
    scalingType: string | undefined,
    colour: string | undefined,
    shape?: string,
  ): void {
    this.emit(from, to, resolveProjectile(shape, scalingType), parseHex(colour, defaultColour(scalingType)));
  }

  /**
   * A **basic** ranged attack: a monster's shot, or a player's bow or wand.
   *
   * The same decoration-over-a-settled-outcome a cast is — the server resolved the
   * hit on the tick it sent the event — so it reuses everything below and differs
   * only in where the shape and the colour come from. It is **not** routed through
   * `resolveProjectile`: that function's first question is whether a *skill's*
   * scaling type throws, and neither a monster nor a bow has a scaling type. Borrowing
   * one would put the player's spell vocabulary on a bow.
   *
   * `shape` is the caller's, and it defaults to the shard every monster fires. A
   * *player's* shot has something behind it — the snapshot carries the shooter's
   * `weapon_type` — so an arrow and a wand bolt differ, and `shotShapeFor` in `bolt.ts` is
   * where that lives. It stays out of here because this class knows about geometry, not
   * about gear.
   */
  shoot(
    from: { x: number; z: number },
    to: { x: number; z: number },
    colour: string | null,
    shape: ProjectileShape = MOB_SHOT_SHAPE,
  ): void {
    this.emit(from, to, projectileVisual(shape), parseHex(colour ?? undefined, MOB_SHOT_RGB));
  }

  /** The shared build: geometry, trail and impact for one projectile. */
  private emit(
    from: { x: number; z: number },
    to: { x: number; z: number },
    visual: ProjectileVisual,
    rgb: [number, number, number],
  ): void {
    const thrown = visual.geometry !== 'none';

    // A thrown bolt with no direction — no target and no aim — has nowhere to fly.
    // One that blooms on the caster does not need one.
    if (thrown && from.x === to.x && from.z === to.z) return;
    // Oldest first, so a burst of casts drops the bolt already nearly landed
    // rather than refusing the one the player just fired.
    if (this.live.length >= MAX_BOLTS) this.retire(0);

    const material = new StandardMaterial('bolt', this.scene);
    // Fully emissive: the floor is near-black and a lit sphere would be invisible.
    material.emissiveColor = new Color3(...rgb);
    material.diffuseColor = new Color3(0, 0, 0);
    material.specularColor = new Color3(0, 0, 0);
    material.disableLighting = true;
    material.alpha = 1;

    const heading = boltHeading(from, to);

    // The flash is built now and hidden, rather than created on arrival: allocating a
    // mesh on the frame a bolt lands is a hitch in the middle of the feedback it exists
    // to give.
    const flash = MeshBuilder.CreateSphere(
      'bolt-flash',
      { diameter: IMPACT_BASE_DIAMETER, segments: 6 },
      this.scene,
    );
    flash.material = material;
    flash.isPickable = false;
    flash.rotation.y = heading;
    flash.setEnabled(false);
    // The impact, the bolt and its trail are one light and bloom together — they share a
    // material, so registering them separately is only about which meshes exist.
    registerGlow(this.glow, flash, 'spell');

    let head: Mesh | null = null;
    const motes: Mesh[] = [];

    if (thrown) {
      head = buildHead(this.scene, visual);
      head.material = material;
      head.isPickable = false;
      registerGlow(this.glow, head, 'spell');
      head.rotation.y = heading;
      boltAt(from, to, 0, this.at);
      head.position.set(this.at.x, this.at.y, this.at.z);

      for (let i = 0; i < visual.trailCount; i++) {
        const mote = MeshBuilder.CreateSphere(
          'bolt-mote',
          { diameter: TRAIL_MOTE_DIAMETER, segments: 4 },
          this.scene,
        );
        mote.material = material;
        mote.isPickable = false;
        registerGlow(this.glow, mote, 'spell');
        mote.scaling.setAll(trailMoteScale(visual, i));
        mote.position.set(this.at.x, this.at.y, this.at.z);
        motes.push(mote);
      }
    } else {
      // Nothing flies, so the flash is at the caster and starts immediately.
      flash.position.set(from.x, BOLT_HEIGHT, from.z);
      flash.setEnabled(true);
    }

    this.live.push({
      visual,
      head,
      motes,
      flash,
      material,
      from,
      to,
      heading,
      age: 0,
      impacting: !thrown,
    });
  }

  /** Advances every bolt. Called once per frame from the render loop. */
  update(dt: number): void {
    this.updateBlasts(dt);

    for (let i = this.live.length - 1; i >= 0; i--) {
      const bolt = this.live[i];
      if (!bolt) continue;
      bolt.age += dt;

      if (!bolt.impacting) {
        const t = bolt.age / BOLT_SECONDS;
        if (t >= 1) {
          // Arrival: become a flash at the destination. The bolt has to *land*, so
          // this happens on the frame it would have overshot, never later.
          this.arrive(bolt);
        } else {
          this.fly(bolt, t);
        }
        continue;
      }

      const t = bolt.age / IMPACT_SECONDS;
      if (t >= 1) {
        this.retire(i);
        continue;
      }
      const s = impactScale(t);
      // Non-uniform on purpose: the same sphere is a vertical shatter for a shard, a
      // flat disc for an arc and a punch along the flight for a beam, and the yaw the
      // flash was built with is what makes "along" mean along.
      bolt.flash.scaling.set(
        s * bolt.visual.impactAcross,
        s * bolt.visual.impactUp,
        s * bolt.visual.impactAlong,
      );
      bolt.material.alpha = impactAlpha(t);
    }
  }

  /** Drops everything — a zone change, or returning to the title. */
  clear(): void {
    for (let i = this.live.length - 1; i >= 0; i--) this.retire(i);
    // **The rings too.** They live in their own list, so a `clear` that only walked the
    // bolts left every open blast in the scene for ever — one leaked mesh and material
    // per area cast, across a zone change that disposes everything else.
    for (const blast of this.blasts) {
      blast.ring.dispose();
      blast.material.dispose();
    }
    this.blasts.length = 0;
  }

  /**
   * Opens a ring where something struck an area.
   *
   * **The ring is the only thing that says why four monsters took damage at once.** The
   * numbers arrive over each head from the ordinary damage events; without this they read
   * as four unrelated hits in one frame.
   *
   * It opens from a point to exactly the server's radius rather than appearing at full
   * size, because the extent is the information: a player has to be able to see how far
   * the blast reached in order to learn to aim it. Flat on the ground and unpickable —
   * it is drawn over the floor a fight is happening on, and a disc that swallowed clicks
   * would eat them from the monsters standing in it.
   */
  blast(
    at: { x: number; z: number },
    radius: number,
    colour: string | null,
    shape: AoeShape = 'TARGET_RADIAL',
    angleDeg = 0,
    facing = 0,
  ): void {
    if (radius <= 0) return;

    // **The footprint is the shape the damage used**, not a ring for everything. A stomp is
    // a circle on the caster, Ground Slam is a 90-degree wedge along their facing and a
    // fissure is a corridor down it — and a ring drawn over a cone teaches a player to aim
    // the wrong way, every single cast. `SELF` and `NONE` cover no ground and draw nothing.
    const footprint = buildAoeMesh(this.scene, shape, angleDeg);
    if (!footprint) return;

    const tint = parseHex(colour ?? undefined, defaultColour(undefined));
    const material = new StandardMaterial('blast-mat', this.scene);
    material.emissiveColor = new Color3(...tint);
    material.diffuseColor = Color3.Black();
    material.specularColor = Color3.Black();
    material.disableLighting = true;
    material.backFaceCulling = false;

    // **The footprint is a painted decal now, not a flat polygon.**
    //
    // The geometry was right and looked like nothing: a dark red wedge at 55% alpha over a
    // dark floor is legible and inert. The atlas gives it a fire ring, an ice ring, a void
    // portal or a crack pattern chosen from the skill's own palette (`fxFor`), stretched
    // across whatever shape the damage used.
    //
    // `ALPHA_ADD` is what makes the sheet's black background disappear — it is drawn on pure
    // black precisely so that adding it contributes nothing — and it is also why the tint
    // multiplies rather than replaces: an additive sprite in the skill's colour reads as that
    // spell's fire, where a white one would read as everybody's.
    const sprite = spriteTexture(this.scene, fxFor(colour).ground);
    material.emissiveTexture = sprite;
    material.opacityTexture = sprite;
    material.alphaMode = Engine.ALPHA_ADD;
    material.alpha = 0.9;
    // **The tint keeps the skill's hue and drops its darkness.**
    //
    // An additive sprite is *multiplied* by the emissive colour, and half the catalogue's
    // palette is dark: Ground Slam is `#8B4513`, so the fire ring came out a muddy brown
    // barely brighter than the floor it was drawn on. Normalising to full value keeps a fire
    // skill orange and a frost skill blue — which is what the palette is for — without
    // letting a dark entry decide how bright the effect is. Brightness belongs to the
    // effect; hue belongs to the skill.
    material.emissiveColor = new Color3(...brighten(tint));

    const ring = footprint;
    ring.material = material;
    ring.isPickable = false;
    ring.position.set(at.x, BLAST_HEIGHT, at.z);
    // A cone and a corridor are built along +Z from their pivot, so the caster's bearing
    // *is* the rotation. A circle ignores it, which is why it can share the code path.
    ring.rotation.y = facing;
    // Grown from a point rather than appearing at full size: the extent is the information,
    // and a player has to see how far it reached to learn to aim it. A corridor grows
    // forward only — its width is fixed in world units and must not scale with its length.
    if (shape === 'LINE') {
      ring.scaling.set(1, 1, 0.05);
    } else {
      ring.scaling.setAll(0.05);
    }
    this.blasts.push({ ring, material, radius, age: 0, shape });
    registerGlow(this.glow, ring, 'spell');
    return;

  }

  dispose(): void {
    this.clear();
  }

  /** Advances every open ring, retiring the ones that have finished. */
  private updateBlasts(dt: number): void {
    for (let i = this.blasts.length - 1; i >= 0; i--) {
      const blast = this.blasts[i];
      if (!blast) continue;
      blast.age += dt;

      const t = blast.age / BLAST_SECONDS;
      if (t >= 1) {
        blast.ring.dispose();
        blast.material.dispose();
        this.blasts.splice(i, 1);
        continue;
      }

      // Opens fast and settles, so the extent is legible for most of its life rather than
      // only at the very end. Every footprint is built at unit extent, so scaling by the
      // radius puts its edge exactly on it.
      const eased = 1 - (1 - t) * (1 - t);
      const reach = Math.max(0.05, blast.radius * eased);
      if (blast.shape === 'LINE') {
        // **Forward only.** A corridor's width is fixed in world units — it is
        // `LineHalfWidth` on the server — so scaling it with the length would make a long
        // fissure a wide one and stop matching where the damage went.
        blast.ring.scaling.set(1, 1, reach);
      } else {
        blast.ring.scaling.setAll(reach);
      }
      // Fades over its life. Additive, so this is a brightness rather than a coverage: the
      // sprite dims out of the scene instead of turning into a grey square.
      blast.material.alpha = 0.9 * (1 - t);
    }
  }

  /** Moves the head and its wake to where they are at `t` of the flight. */
  private fly(bolt: Flying, t: number): void {
    const { visual, head } = bolt;
    if (!head) return;

    boltAt(bolt.from, bolt.to, t, this.at);
    head.position.set(this.at.x, this.at.y, this.at.z);
    // Rolled about the flight axis. Written every frame rather than accumulated so a
    // dropped frame does not leave the head at a different angle than the maths says.
    if (visual.spin !== 0) head.rotation.z = bolt.age * visual.spin;

    if (visual.trailCount === 0) return;
    // Perpendicular to the flight, on the floor plane. The heading yaws +Z along the
    // path, so the lateral axis is its sine/cosine swapped.
    const sideX = Math.cos(bolt.heading);
    const sideZ = -Math.sin(bolt.heading);

    for (let i = 0; i < bolt.motes.length; i++) {
      const mote = bolt.motes[i];
      if (!mote) continue;
      boltAt(bolt.from, bolt.to, t - trailDelay(visual, i), this.at);
      const off = trailOffset(visual, i);
      mote.position.set(this.at.x + sideX * off, this.at.y, this.at.z + sideZ * off);
    }
  }

  /** Retires the projectile and hands the bolt over to its flash. */
  private arrive(bolt: Flying): void {
    bolt.impacting = true;
    bolt.age = 0;

    boltAt(bolt.from, bolt.to, 1, this.at);
    bolt.flash.position.set(this.at.x, this.at.y, this.at.z);
    bolt.flash.setEnabled(true);

    // Disabled rather than disposed: the bolt is about to be retired anyway, and
    // disposing mid-frame is work in the middle of the frame the player is watching.
    bolt.head?.setEnabled(false);
    for (const mote of bolt.motes) mote.setEnabled(false);
  }

  private retire(index: number): void {
    const bolt = this.live[index];
    if (!bolt) return;
    bolt.head?.dispose();
    for (const mote of bolt.motes) mote.dispose();
    bolt.flash.dispose();
    // Each bolt owns its material because each has its own colour and fading
    // alpha; sharing one would fade every bolt on screen together.
    bolt.material.dispose();
    this.live.splice(index, 1);
  }
}

/**
 * Builds one projectile head, oriented so its long axis is local +Z.
 *
 * Exported for `bolts.test.ts`, which measures the built bounding boxes under
 * `NullEngine` and asserts every pair of shapes differs by more than an output pixel.
 * A shape a player cannot tell from another one is not progress, and the numbers in
 * `bolt.ts` are not the shape — the geometry built from them is.
 *
 * Every builder used here has to be named in `engine.ts`'s side-effect list, which is
 * that file's one statement of what geometry this scene builds — `engine.test.ts`
 * compares it against every `MeshBuilder.Create*` in the tree, in both directions.
 */
/**
 * A colour at full value, keeping its hue.
 *
 * Black returns white rather than dividing by zero. A skill with no palette already falls
 * back to the field's neutral before this is reached, so the guard is for safety.
 */
function brighten(rgb: readonly [number, number, number]): [number, number, number] {
  const max = Math.max(rgb[0], rgb[1], rgb[2]);
  if (max <= 0.001) return [1, 1, 1];
  return [rgb[0] / max, rgb[1] / max, rgb[2] / max];
}

export function buildHead(scene: Scene, visual: ProjectileVisual): Mesh {
  switch (visual.geometry) {
    case 'spike': {
      // A four-sided cone: a splinter. The low tessellation is the point — at eight
      // output pixels per unit a smooth cone and a faceted one are the same picture, and
      // the facets catch the emissive term unevenly, which is what reads as ice.
      const spike = MeshBuilder.CreateCylinder(
        'bolt-shard',
        { height: visual.along, diameterTop: 0, diameterBottom: visual.across, tessellation: 4 },
        scene,
      );
      return lieAlongFlight(spike);
    }

    case 'lance': {
      const lance = MeshBuilder.CreateCylinder(
        'bolt-lance',
        { height: visual.along, diameter: visual.across, tessellation: 6 },
        scene,
      );
      return lieAlongFlight(lance);
    }

    // A thin sliver and a wide flat fan are the same builder with the width and the
    // depth swapped, which is exactly the difference a player sees: one is a splinter
    // of light travelling point-first, the other is a front travelling broadside.
    case 'sliver':
      return MeshBuilder.CreateBox(
        'bolt-arc',
        { width: visual.across, height: visual.up, depth: visual.along },
        scene,
      );

    case 'fan':
      return MeshBuilder.CreateBox(
        'bolt-spray',
        { width: visual.across, height: visual.up, depth: visual.along },
        scene,
      );

    case 'sphere':
    default:
      return MeshBuilder.CreateSphere('bolt-orb', { diameter: visual.across, segments: 6 }, scene);
  }
}

/**
 * Turns a cylinder's long axis from +Y to +Z, in its vertices.
 *
 * Baked rather than set as `rotation.x`, because the transform slots are already spoken
 * for: `rotation.y` is the heading and `rotation.z` is the roll. Babylon composes those
 * as yaw-pitch-roll, so a pitch of 90° between them would turn the roll into a tumble —
 * a shard spinning end over end instead of about its own length. Baking also means the
 * bounding box `bolts.test.ts` measures is the shape as drawn.
 */
function lieAlongFlight(mesh: Mesh): Mesh {
  mesh.bakeTransformIntoVertices(Matrix.RotationX(Math.PI / 2));
  return mesh;
}
